import { type Qualification } from "@clipboard-health/contract-worker-app-bff";
import { isDefined } from "@clipboard-health/util-ts";

import { type OnboardingRequirement } from "../../../../PlacementCandidate/hooks/useOnboardingRequirement";
import {
  type PlacementCandidate,
  PlacementCandidateScreeningStatus,
} from "../../../../PlacementCandidate/types";
import { type InterviewAvailability } from "../../../api/useGetInterviewAvailability";
import { type PlacementDetail } from "../../../api/useGetPlacement";
import { type TalkToRecruiterCopy } from "../../../hooks/useTalkToRecruiterCopyText";
import {
  doesPlacementHasOpenHouse,
  hasAppliedToPlacement,
  hasAttendedInterview,
  hasBookedInterview,
  hasBookedInterviewInFuture,
  interviewRequired,
  isApplicationCompleted,
} from "../../../utils";
import { getVerifiedType } from "../../../utils/verified-placement";
import { type PrimaryActionParams } from "./getPrimaryAction";

export interface BuildPrimaryActionParamsOptions {
  isLoadingInterviewAvailability: boolean;
  isProfileCompleted: boolean;
  isProfilePending: boolean;
  isProfileInvalid: boolean;
  requirement?: OnboardingRequirement;
  talkToRecruiterCopy: TalkToRecruiterCopy;
  isJobInterviewsEnabled: boolean;
  isJobRequestEnabled: boolean;
  isHiringReferralEnabled: boolean;
  maxReferralBonusAmount: number;
  isOnboardingRequirementsLoading: boolean;
  isGetIncentivesLoading: boolean;
  isReschedulingPastInterview: boolean;
  simplifiedBanEnabled: boolean;
  startsInWorkplaceToday?: boolean;
  bookedInterviewType?: "in-person-interview" | "phone-screen";
  isChildcareSupportChatEnabled: boolean;
  autoApprovedQualifications: Set<string>;
  workerShoppableQualificationIds: string[];
  allQualifications: Qualification[];
  isQualificationsLoading: boolean;
}

/**
 * Find the first placement worker type that:
 * 1. Is in the auto-approved qualifications list (from feature flag)
 * 2. Worker doesn't already have it in their shoppable qualifications
 *
 * Returns the qualification name, ID, and display label if found.
 */
function findMissingAutoApprovedQualification(
  placementWorkerTypes: string[] | undefined,
  workerShoppableQualificationIds: string[],
  autoApprovedQualifications: Set<string>,
  allQualifications: Qualification[]
): { name?: string; id?: string; displayLabel?: string } {
  if (!placementWorkerTypes || placementWorkerTypes.length === 0) {
    return {};
  }

  // Find first placement worker type that is auto-approved and worker doesn't have
  for (const workerType of placementWorkerTypes) {
    if (autoApprovedQualifications.has(workerType)) {
      // Find this qualification in all qualifications to get ID and display label
      const qualification = allQualifications.find((q) => q.attributes.name === workerType);
      if (qualification && !workerShoppableQualificationIds.includes(qualification.id)) {
        return {
          name: workerType,
          id: qualification.id,
          displayLabel: qualification.attributes.displayLabel,
        };
      }
    }
  }

  return {};
}

export function buildPrimaryActionParams(
  placementDetails: PlacementDetail,
  placementCandidate: PlacementCandidate,
  interviewAvailability: InterviewAvailability,
  options: BuildPrimaryActionParamsOptions
): PrimaryActionParams {
  const {
    isLoadingInterviewAvailability,
    isProfileCompleted,
    isProfilePending,
    isProfileInvalid,
    requirement,
    talkToRecruiterCopy,
    isJobInterviewsEnabled,
    isJobRequestEnabled,
    isHiringReferralEnabled,
    maxReferralBonusAmount,
    isOnboardingRequirementsLoading,
    isGetIncentivesLoading,
    isReschedulingPastInterview,
    simplifiedBanEnabled,
    startsInWorkplaceToday,
    bookedInterviewType,
    isChildcareSupportChatEnabled,
    autoApprovedQualifications,
    workerShoppableQualificationIds,
    allQualifications,
    isQualificationsLoading,
  } = options;

  // Derive placement state
  const verifiedType = getVerifiedType(placementDetails);
  const requiredInterview = interviewRequired(placementDetails);
  const bookedInterview = hasBookedInterview(placementDetails);
  const bookedInterviewInFuture = hasBookedInterviewInFuture(placementDetails);
  const attendedInterview = hasAttendedInterview(placementDetails);
  const applicationCompleted = isApplicationCompleted(placementDetails);
  const hasOpenHouse = doesPlacementHasOpenHouse(placementDetails);
  const isWaitlist = placementDetails.status === "waitlist";
  const hasApplied = hasAppliedToPlacement(placementDetails);

  // Use the actual booked interview type if available, otherwise fall back to workplace settings
  const selfServeInterviewBookingType =
    bookedInterviewType ??
    placementDetails.interviewSettings?.attributes.selfServeInterviewBookingType ??
    "in-person-interview";

  // Derive document requirements state
  const missingDocuments = placementDetails.workerRequirements?.filter(
    (request) => !request.attributes.isValid
  );
  const hasOnlyInvisibleHCPInvalidDocuments =
    isDefined(missingDocuments) &&
    missingDocuments.length > 0 &&
    missingDocuments.every(
      (request) => !request.relationships.requirement.data.attributes.visibleToHCP
    );

  // Derive candidate screening state
  const screeningStatus =
    placementCandidate.screeningStatus ?? PlacementCandidateScreeningStatus.NO_SCREENING;
  const isExcellentCandidate =
    screeningStatus === PlacementCandidateScreeningStatus.PASSED_EXCEPTIONALLY;

  // Derive loading state
  const isLoading =
    isLoadingInterviewAvailability ||
    isOnboardingRequirementsLoading ||
    isGetIncentivesLoading ||
    isQualificationsLoading;

  // Find missing auto-approved qualification
  const missingAutoApprovedQualification = findMissingAutoApprovedQualification(
    placementDetails.workerTypes ?? [],
    workerShoppableQualificationIds,
    autoApprovedQualifications,
    allQualifications
  );

  return {
    hasApplied,
    hasInterview: bookedInterview,
    hasInterviewInFuture: bookedInterviewInFuture,
    applicationCompleted,
    hasInterviewAvailability: interviewAvailability.length > 0,
    isLoadingInterviewAvailability,
    isProfileComplete: isProfileCompleted,
    isProfilePending,
    isProfileInvalid,
    requirementDisplayName: requirement?.getDisplayName() ?? "profile",
    requirementSubItemsCount: requirement?.subItems?.length ?? 0,
    hasRequiredLicenses: placementDetails.isLicenseEligible,
    workplaceState: placementDetails.workplace.state,
    hasRequiredDocuments:
      !isDefined(placementDetails.isDocumentRequirementValid) ||
      placementDetails.isDocumentRequirementValid,
    hasOnlyInvisibleHCPInvalidDocuments,
    isExemptFromDocumentRequirements: placementDetails.isExemptFromDocumentRequirements ?? false,
    isDocumentRequirementValid: placementDetails.isDocumentRequirementValid,
    bookedInterview,
    attendedInterview,
    isProspectWorkplace: Boolean(placementDetails.workplace.placementSettings?.isProspect),
    requiredInterview,
    hasOpenHouse,
    screeningStatus,
    isRecruiterScreeningEnabled: placementDetails.isRecruiterScreeningEnabled ?? false,
    talkToRecruiterLabel: talkToRecruiterCopy.placementAction.screening,
    talkToRecruiterDescription: talkToRecruiterCopy.placementAction.screeningDescription,
    talkToRecruiterUpskillingLabel: talkToRecruiterCopy.placementAction.upskilling,
    talkToRecruiterUpskillingDescription: talkToRecruiterCopy.placementAction.upskillingDescription,
    rescheduleRecruiterLabel: "Reschedule recruiter call",
    isJobInterviewsEnabled,
    isJobRequestEnabled,
    verifiedType,
    isWaitlist,
    isRestricted: placementCandidate.isRestricted ?? false,
    isHiringReferralEnabled,
    maxReferralBonusAmount,
    hasWebApplicationLink: Boolean(placementDetails.webApplicationLink),
    isExcellentCandidate,
    isLoading,
    isReschedulingPastInterview,
    simplifiedBanEnabled,
    startsInWorkplaceToday,
    bookingReorderingCohort: placementCandidate.bookingReorderingCohort,
    selfServeInterviewBookingType,
    isChildcareSupportChatEnabled,
    missingAutoApprovedQualificationName: missingAutoApprovedQualification.name,
    missingAutoApprovedQualificationDisplayLabel: missingAutoApprovedQualification.displayLabel,
    missingAutoApprovedQualificationId: missingAutoApprovedQualification.id,
  };
}
